<template>
  <div :class="fieldClass" class="base-field">
    <label v-if="label" class="base-field__label">
      <span v-if="isRequired" class="base-field_required">*</span>
      {{ label }}
    </label>
    <div class="base-field__control">
      <div v-if="isSearch" class="mr-2 flex justify-center items-center search-icon-container">
        <SearchIcon />
      </div>
      <input :value="isDatepicker ? (calendarValue ? new Date(calendarValue).toLocaleDateString('en-US') : '') : value"
        class="base-field__input" :autocomplete="isDatepicker ? 'off' : 'on'" :placeholder="placeholder"
        :readonly="isReadonly || isDatepicker" :name="name" :type="type" :maxlength="maxLength"
        :inputmode="isNumericOnly ? 'numeric' : null" @click="toggleCalendar" @input="handleInput"
        @paste="handlePaste" />
      <Calendar v-if="isDatepicker && showCalendar" v-model="calendarValue" @input="handleTetstDateSelect" inline
        :maxDate="maxDate" :minDate="minDate" appendTo="body" dateFormat="dd/mm/yy" @date-select="handleDateSelect"
        :monthNavigator="true" v-click-outside="closeCalendar" />
      <div class="base-field__slot">
        <button type="button">
          <span v-if="isPassword" class="ui-kit__icon" :class="passwordFieldIcon" @click="showPassword"></span>
          <span v-if="isSearch && value && !isOrg" @click="$emit('update:value', '')"
            class="ui-kit__icon text-foregroundPrimary isSearchIcon">
            &times;
          </span>
          <span v-if="isClearable && value && !isOrg" @click="$emit('update:value', '')"
            class="ui-kit__icon text-foregroundPrimary">
            &times;
          </span>
          <span v-if="isOrg" @click="$emit('clearMembers')" class="ui-kit__icon text-foregroundPrimary"> &times; </span>
          <span v-if="isCheckIcon" class="ui-kit__icon text-foregroundPrimary cursor-default">
            <CircleCheckIcon />
          </span>
        </button>
        <slot name="button"></slot>
      </div>
    </div>
    <TransitionGroup>
      <div class="base-field__error" v-for="(item, idx) of errorsList" :key="idx">
        <span class="base-field__error-message">{{ formatError(item) }}</span>
      </div>
    </TransitionGroup>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';

import { directive as vClickOutside } from 'click-outside-vue3';
import Calendar from 'primevue/calendar';
import { icons } from '@tiptap/pm/menu';
import SearchIcon from '@/components/Icons/SearchIcon.vue';
import CircleCheckIcon from '@/components/Icons/CircleCheckIcon.vue';

const emit = defineEmits(['update:value', 'showPassword', 'clearMembers']);
const props = defineProps({
  value: {
    type: String,
    default: '',
  },
  text: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: 'text',
  },
  name: {
    type: String,
    default: '',
  },
  maxLength: {
    type: Number,
    default: null,
  },
  error: {
    type: Array,
    required: false,
  },
  isPassword: {
    type: Boolean,
    default: false,
  },
  isReadonly: {
    type: Boolean,
    default: false,
  },
  isTextCenter: {
    type: Boolean,
    default: false,
  },
  isGray: {
    type: Boolean,
    default: false,
  },
  isRequired: {
    type: Boolean,
    default: false,
  },
  isBorder: {
    type: Boolean,
    default: false,
  },
  isBottomBorderOnFocus: {
    type: Boolean,
    default: false,
  },
  isSearch: {
    type: Boolean,
    default: false,
  },
  isOrg: {
    type: Boolean,
    default: false,
  },
  isClearable: {
    type: Boolean,
    default: false,
  },
  isDatepicker: {
    type: Boolean,
    default: false,
  },
  isCheckIcon: {
    type: Boolean,
    default: false,
  },
  isNumericOnly: {
    type: Boolean,
    default: false,
  },
});
// PrimeVue Calendar expects Date or Date[]; keep internal as Date | null
const calendarValue = ref(props.value ? new Date(props.value) : null);
const showCalendar = ref(false);
const maxDate = ref(new Date());
const minDate = ref(new Date(1900, 0, 1));

watch(
  () => props.value,
  newValue => {
    if (props.isDatepicker) {
      calendarValue.value = newValue ? new Date(newValue) : null;
    }
  },
);

const fieldClass = computed(() => {
  return [
    {
      'base-field_is-readonly': props.isReadonly,
      'base-field_is-password': props.type === 'password',
      'base-field_is-center': props.isTextCenter,
      'base-field_is-border': props.isBorder,
      'base-field_is-gray': props.isGray,
      'base-field_is-search': props.isSearch,
      'base-field_is-bottom-border-focus': props.isBottomBorderOnFocus,
    },
  ];
});

const passwordFieldIcon = computed(() => {
  return props.type === 'password' ? 'icon-eye-off' : 'icon-eye';
});

// Normalize errors to strings for rendering and to satisfy TS template inference
const errorsList = computed(() => Array.isArray(props.error) ? props.error : []);
const formatError = (item) => {
  try {
    if (item && typeof item === 'object' && '$message' in item) {
      return item.$message;
    }
    return String(item ?? '');
  } catch {
    return '';
  }
};

const handlePaste = e => {
  if (!props.isNumericOnly) return;

  const cd = e.clipboardData;
  const pasted = cd ? cd.getData('text') : '';
  if (!/^\d+$/.test(pasted)) {
    e.preventDefault();
  }
};

const handleInput = e => {
  let val = e.target.value;
  if (props.isNumericOnly) {
    val = val.replace(/\D/g, '');
    e.target.value = val;
  }
  emit('update:value', val);
};

const showPassword = e => {
  emit('showPassword', e.target.value);
};
const toggleCalendar = () => {
  if (props.isDatepicker) {
    showCalendar.value = !showCalendar.value;
  }
};
const closeCalendar = () => {
  showCalendar.value = false;
};

const handleTetstDateSelect = () => { };
const handleDateSelect = () => {
  const d = calendarValue.value instanceof Date ? calendarValue.value : null;
  const formattedDate = d ? d.toLocaleDateString('en-US') : '';
  emit('update:value', formattedDate);
  closeCalendar();
};
</script>

<style lang="scss" scoped>
.isSearchIcon {
  font-size: 26px;
  opacity: 0.4;
  transition: 0.3s;

  &:hover {
    opacity: 1;
  }
}

.base-field {
  position: relative;
  font-size: 20px;
  // margin-bottom: 10px;

  &__label {
    color: var(--text-02);
    min-height: 30px;
    margin-bottom: 0;
    font-size: 14px;
  }

  &_required {
    color: var(--interactive-01);
  }

  &__control {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    height: 48px;
    padding: 0 15px;
    line-height: 145%;
    border: 1px solid var(--border-subtle);
    border-radius: 12px;
    transition: 0.2s ease-in-out;

    &:focus-within {
      border-color: var(--input-field-border);
    }
  }

  &__input {
    position: relative;
    width: 100%;
    border: none;
    background: transparent;
    color: var(--input-text);
    font-size: 16px;

    &::placeholder {
      color: var(--input-text);
      font-size: 16px;
    }
  }

  &__slot {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 4px 0;
    height: 100%;

    span {
      width: 25px;

      &:before {
        font-size: 26px;
      }
    }
  }

  &__error {
    margin-top: 2px;
    border-radius: 10px;
    font-size: 12px;
    color: var(--interactive-01);
    padding: 2px 10px;
  }

  .ui-kit {
    &__icon {
      &::before {
        font-size: 20px;
      }
    }
  }

  &_is-password {
    &:focus-within {
      .ui-kit {
        &__icon {
          &::before {
            color: var(--interactive-01);
          }
        }
      }
    }
  }

  &_is-readonly {
    .base-field__control {
      background: rgba(255, 255, 255, 0.05);
      cursor: not-allowed;
    }

    .base-field__input {
      &:read-only {
        color: var(--text-02);
        cursor: not-allowed;
      }
    }
  }

  &_is-border {
    .base-field__control {
      border: 1px solid var(--border-subtle);

      &:focus-within {
        border-color: var(--interactive-01);
      }
    }
  }

  &_is-bottom-border-focus {
    gap: 8px;
    display: flex;
    flex-direction: column;

    .base-field__label {
      font-size: 12px;
      line-height: 16px;
      min-height: auto;
    }

    .base-field__control {
      background: var(--background-secondary);
      border: none;
      border-bottom: 1px solid transparent;
      transition: border-bottom 0.3s ease;
    }

    .base-field__control:focus-within {
      border: none;
      border-bottom: 1px solid var(--foreground-primary);
    }

    .base-field__input {
      font-size: 16px;
      line-height: 20px;
      color: var(--foreground-primary);
    }

    .base-field__input::placeholder {
      font-size: 16px;
      line-height: 20px;
      color: #9696a2;
    }

    .base-field__error {
      color: #aa2c4e;
      padding: 0;
    }

    .base-field__control:has(+ .base-field__error) {
      border: none;
      border-bottom: 1px solid #aa2c4e;
    }
  }

  &_is-search {
    .base-field__control {
      background: var(--search-bg);
      border-color: var(--surface-border, var(--input-field-border));
      box-shadow: inset 0 0 0 1px var(--surface-border, var(--search-bg));
      transition: background .15s ease, box-shadow .15s ease, border-color .15s ease;
    }

    .base-field__control:focus-within {
      border-color: var(--input-field-border);
    }

    .base-field__control:hover:not(:focus-within) {
      box-shadow: inset 0 0 0 1px var(--border-primary, #cbd5e1);
      // background: var(--input-field-border);
    }

    .base-field__input {
      color: var(--search-text-color);
    }

    .base-field__input::placeholder {
      color: var(--input-text);
    }

    .base-field__control:focus-within {
      background: var(--background-primary);
    }

    /* Dark mode refinement */
    [scheme='dark'] & .base-field__control {
      background: rgba(255, 255, 255, 0.05);
      box-shadow: inset 0 0 0 1px var(--surface-border, #334155);
    }

    [scheme='dark'] & .base-field__control:hover:not(:focus-within) {
      background: rgba(255, 255, 255, 0.08);
    }

    [scheme='dark'] & .base-field__control:focus-within {
      background: var(--background-secondary);
      // box-shadow: 0 0 0 2px rgba(132, 204, 22, 0.35), inset 0 0 0 1px var(--interactive-01);
    }
  }

  &_is-gray {
    .base-field__control {
      background: var(--background-secondary);
    }
  }

  &_is-center &__input {
    text-align: center;
  }

  &_is-center {
    min-width: 100px;
  }

  @include _380 {
    font-size: 14px;

    &__control {
      font-size: 14px;
      height: 44px;
    }
  }
}



.p-calendar {
  position: absolute;
  z-index: 1000;
  bottom: 50px;
  left: 50%;
  transform: translateX(-50%);

  &>* {
    color: var(--foreground-primary);
  }
}

.p-calendar>.p-datepicker-calendar tbody>tr>td>span {
  background-color: var(--background-secondary) !important;
}

.icon-eye {
  background: url('/icon/eye.svg') no-repeat center;
  width: 16px;
  height: 16px;
  display: block;
}

.icon-eye-off {
  background: url('/icon/eye-off.svg') no-repeat center;
  width: 16px;
  height: 16px;
  display: block;
}

.search-icon-container {
  color: var(--text-01);
  opacity: 0.8;

  /* Ensure better visibility in light mode */
  [scheme='light'] & {
    opacity: 0.9;
    color: var(--foreground-primary);
  }

  /* Maintain good visibility in dark mode */
  [scheme='dark'] & {
    opacity: 0.7;
    color: var(--text-01);
  }
}
</style>
